// src/Pages/Logs/LogsListPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Button, Form, Badge } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { LogsDataService } from '../../Services/LogsDataService';
import { SchedulerDataService } from '../../Services/SchedulerDataService';   // NEW
import Table from '../../Components/Table/Table';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faMagnifyingGlass, faPlay } from '@fortawesome/free-solid-svg-icons';
import LogsDetailsModal from './LogsDetailsModal';
import TaskConfigModal from '../SchedulerTaskListPage/TaskConfigModal';       // NEW
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';

const ResultadoBadge = ({ value }) => {
  const map = { EXITO: 'success', ERROR: 'danger', INICIO: 'info' };
  return <Badge bg={map[value] || 'secondary'}>{value}</Badge>;
};

const toUtcIsoStart = (yyyyMmDd) => (yyyyMmDd ? new Date(`${yyyyMmDd}T00:00:00Z`).toISOString() : null);
const toUtcIsoEnd   = (yyyyMmDd) => (yyyyMmDd ? new Date(`${yyyyMmDd}T23:59:59Z`).toISOString() : null);

const LogsListPageContent = () => {
  const { instance, accounts, inProgress } = useMsal();

  // Filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [tipo, setTipo] = useState('');
  const [objeto, setObjeto] = useState('');
  const [resultado, setResultado] = useState('');
  const [q, setQ] = useState('');

  // Paging
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  // Data
  const [rows, setRows] = useState([]);

  // Details
  const [showDetails, setShowDetails] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // NEW: ad-hoc run modal & spinner
  const [showRunModal, setShowRunModal] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (inProgress === 'none' && accounts.length > 0) fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, accounts, page, pageSize]);

  const token = async () => {
    const request = { ...loginRequest, account: accounts[0] };
    return (await instance.acquireTokenSilent(request)).accessToken;
  };

  const fetchLogs = async () => {
    try {
      const { items, total } = await LogsDataService.getLogs(await token(), {
        from: toUtcIsoStart(from),
        to: toUtcIsoEnd(to),
        tipo, objeto, resultado, q, page, pageSize
      });
      setRows(items || []);
      setTotal(total || 0);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    }
  };

  const runAdHoc = async (meta) => {
    // meta = { taskType, parameters: JSON-string }
    try {
      setRunning(true);
      await SchedulerDataService.executeAdHocTask(meta, await token());
      setShowRunModal(false);
      // Give the backend a short moment to write logs (optional small delay)
      setTimeout(fetchLogs, 600);
    } catch (err) {
      console.error('Failed to execute task:', err);
      setShowRunModal(false);
    } finally {
      setRunning(false);
    }
  };

  const columns = useMemo(() => [
    { header: 'Id', accessorKey: 'id' },
    {
      header: 'Fecha',
      accessorKey: 'fecha',
      cell: info => formatGuatemalanDateTime(info.getValue())
    },
    { header: 'Tipo', accessorKey: 'tipo' },
    { header: 'Objeto', accessorKey: 'objeto' },
    {
      header: 'Resultado',
      accessorKey: 'resultado',
      cell: info => <ResultadoBadge value={info.getValue()} />
    },
    {
      header: 'Detalles',
      id: 'det',
      cell: ({ row }) => (
        <Button size="sm" variant="outline-secondary"
                onClick={() => { setSelectedLog(row.original); setShowDetails(true); }}>
          <FontAwesomeIcon icon={faMagnifyingGlass} /> View
        </Button>
      )
    }
  ], []);

  const pagerText = () => {
    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return `${start}-${end} of ${total}`;
  };

  return (
    <Container fluid className="h-100 d-flex flex-column">
      {/* Filters toolbar omitted for brevity (keep your existing one) */}

      <div className="d-flex justify-content-end align-items-center mt-2 mb-2 gap-2">
        <Button variant="outline-primary" onClick={() => { setPage(1); fetchLogs(); }}>
          <FontAwesomeIcon icon={faRefresh} />
        </Button>

        {/* NEW: Run task now */}
        <Button
          variant="primary"
          disabled={running}
          onClick={() => setShowRunModal(true)}
          title="Run an ad-hoc extraction now"
        >
          <FontAwesomeIcon icon={faPlay} className="me-2" />
          {running ? 'Running…' : 'Run task'}
        </Button>
      </div>

      {/* Table */}
      <Row className="flex-grow-1">
        <Col>
          <Table
            columns={columns}
            data={rows}
            tableClass="table-hover"
            containerClass="table-responsive"
            headerClass="table-light"
          />
        </Col>
      </Row>

      {/* Details Modal */}
      {showDetails && (
        <LogsDetailsModal
          show={showDetails}
          onHide={() => setShowDetails(false)}
          log={selectedLog}
        />
      )}

      {/* NEW: Task config (step 1 only, no schedule) */}
      {showRunModal && (
        <TaskConfigModal
          show={showRunModal}
          onHide={() => setShowRunModal(false)}
          onNext={runAdHoc}
        />
      )}
    </Container>
  );
};

const LogsListPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Execution Logs">
      <LogsListPageContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default LogsListPage;
